import { Injectable } from '@angular/core';

import { Sprite2D } from '../custom/Sprite2D';
import { ObjectCheckerService } from './object-checker.service';
import { RendererService } from './renderer.service';
import { environment } from '../../environments/environment';

declare const THREE: any;

@Injectable()
export class Model3DService {
  apiUrl: string = environment.apiUrl;

  constructor(
    private objectCheckerSrvc: ObjectCheckerService,
    private renderedSrvc: RendererService
  ) {}

  private cache: Array<any> = [];

  private _modelUrl(entity, fileName: string): string {
    if (entity && entity.localAsset) {
      return `/assets/models/${fileName}`;
    }
    return `${this.apiUrl}/containers/models/download/${fileName}`;
  }

  restoreObjectSize(obj, entity, newSize) {
    if (newSize) {
      obj.scale.set(
        newSize.length / entity.length,
        newSize.width / entity.width,
        newSize.height / entity.height
      );
    }
  }

  private add2DSprite(model) {
    if (this.objectCheckerSrvc.isWallEmbeddable(model)) {
      const { entity } = model.userData;

      const sprite2D = new Sprite2D({
        width: entity.length,
        height: entity.width,
        label: entity.label,
        anisotropy: this.renderedSrvc.getMaxAnisotropy()
      });

      model.add(sprite2D);
    }
  }

  create(entity, params): Promise<any> {
    let promise: Promise<any>;

    // Check if entity is defined
    if (!entity) {
      console.warn('Model3DService: Entity is undefined or null');
      return Promise.reject(new Error('Entity is undefined or null'));
    }

    params = params || {};

    var originalEntity = entity;

    if (params.size) {
      entity = JSON.parse(JSON.stringify(originalEntity));
      entity.length = params.size.length;
      entity.width = params.size.width;
      entity.height = params.size.height;
    }

    var obj3D = params.object3D || null;

    if (obj3D) {
      // obj3D.name = '3D';
      obj3D.name = params.name;
      // container.add(obj3D);
      obj3D.userData.entity = entity;
      obj3D.position.copy(params.position);
      obj3D.rotation.copy(params.rotation);
      promise = Promise.resolve(obj3D);
    } else {
      promise = new Promise((resolve, reject) => {
        for (let i = 0; i < this.cache.length; i++) {
          const model = this.cache[i];
          if (model.userData.entity && entity && model.userData.entity.id === entity.id) {
            let modelClone = new THREE.Object3D();
            for (let i = 0; i < model.children.length; i++) {
              const ch = model.children[i];
              let newCh = new THREE.Mesh(ch.geometry.clone(), ch.material);
              newCh.name = ch.name;
              modelClone.add(newCh);
            }

            modelClone.updateMatrixWorld(true);

            modelClone.userData.entity = JSON.parse(JSON.stringify(originalEntity));

            // to do check if needed
            this.restoreObjectSize(modelClone, originalEntity, params.size);

            if (params.position) modelClone.position.copy(params.position);

            if (params.rotation) modelClone.rotation.copy(params.rotation);

            if (params.wall) modelClone.userData.wall = params.wall;

            resolve(modelClone);
            return;
          }
        }

        if (!originalEntity.modelMtl) {
          const geometry = new THREE.BoxGeometry(
            originalEntity.length,
            originalEntity.height,
            originalEntity.width
          );
          const material = new THREE.MeshLambertMaterial({ color: 0x8f8f8f });
          const cube = new THREE.Mesh(geometry, material);
          params.position && cube.position.copy(params.position);
          params.rotation && cube.rotation.copy(params.rotation);
          cube.userData.entity = entity;
          this.add2DSprite(cube);
          resolve(cube);
          return;
        }

        const mtlUrl = this._modelUrl(originalEntity, originalEntity.modelMtl);
        const objUrl = this._modelUrl(originalEntity, originalEntity.modelObj);

        new THREE.MTLLoader().load(
          mtlUrl,
          materials => {
            if (!originalEntity.localAsset) {
              const materialsInfo = materials.materialsInfo;
              for (const mat in materialsInfo) {
                if (materialsInfo.hasOwnProperty(mat)) {
                  if (materialsInfo[mat].map_ka) {
                    materialsInfo[
                      mat
                    ].map_ka = `${this.apiUrl}/containers/images/download/${materialsInfo[mat].map_ka}`;
                  }
                  if (materialsInfo[mat].map_kd) {
                    materialsInfo[
                      mat
                    ].map_kd = `${this.apiUrl}/containers/images/download/${materialsInfo[mat].map_kd}`;
                  }
                }
              }
            }

            materials.crossOrigin = 'anonymous';
            materials.preload();

            new THREE.OBJLoader().setMaterials(materials).load(
              objUrl,
              model => {
                model.userData.entity = JSON.parse(JSON.stringify(originalEntity));

                this.cache.push(model);

                let newModel = new THREE.Object3D();
                for (let i = 0; i < model.children.length; i++) {
                  const ch = model.children[i];
                  let newCh = new THREE.Mesh(ch.geometry.clone(), ch.material);
                  newCh.name = ch.name;
                  newModel.add(newCh);
                }

                newModel.userData.entity = JSON.parse(JSON.stringify(originalEntity));

                if (params.position) newModel.position.copy(params.position);

                if (params.rotation) newModel.rotation.copy(params.rotation);

                if (params.wall) newModel.userData.wall = params.wall;

                resolve(newModel);
              },
              xhr => {
                // console.log((xhr.loaded / xhr.total * 100) + '% loaded');
              },
              error => {
                // console.log('An error happened');
                console.log(error);
              }
            );
          }
        );
      });
    }
    return promise;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/model3-d.service.ts